import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {select, Store} from '@ngrx/store';
import {Actions} from '@ngrx/effects';
import {GridApi, GridReadyEvent} from 'ag-grid-community';
import {TranslateService} from '@ngx-translate/core';
import {TooltipFieldComponent} from '../tooltip-field/tooltip-field.component';
import {NumberUtils} from '../../../common/utils/number.utils';
import {setFlatGridRole, suppressGridTabToExit} from '../../../common/utils/ag-grid-a11y';
import {Observable} from 'rxjs';


@Component({
  standalone: false,
  selector: 'mswf-rules-dialog',
  templateUrl: './mswf-rules-dialog.component.html',
  styleUrls: ['./mswf-rules-dialog.component.css']
})
export class MswfRulesDialogComponent implements OnInit, OnDestroy {
  private gridApi: GridApi;
  private tabExitHandler?: (event: KeyboardEvent) => void;
  frameworkComponents: any;
  colDefs: any;
  // a11y (ARIA grid pattern / WCAG 2.1.1): cells are reached with Arrow keys
  // only; Tab moves focus out of the grid to the next interactive control.
  // Both body AND header cells must follow the exit model — otherwise Tab from
  // a header cell steps header-to-header (AG Grid default) instead of leaving
  // the grid, so focus never reaches the footer Close button.
  defaultColDef = {
    sortable: false,
    suppressKeyboardEvent: suppressGridTabToExit,
    suppressHeaderKeyboardEvent: suppressGridTabToExit,
  };
  mswfData:any;
  planName: string;
  protected rules: any;

  public language = "en";
  public locale = "en-US";
  public userInfo$: Observable<string>;

  @Input()
  public displayPlanName = true;

  constructor(public activeModal: NgbActiveModal,
              private store: Store<any>,
              private action$: Actions,
              protected translateSvc: TranslateService,
              private modalService: NgbModal,
              private hostEl: ElementRef<HTMLElement>) {
    this.initColumnDefs();
    this.frameworkComponents = {
      TooltipField: TooltipFieldComponent
    }

    this.userInfo$ = this.store.select("state");
    this.userInfo$.subscribe((value: any) => {
      if (value && value.user && value.user.language) {
        if (this.language != value.user.language) {
          this.language = value.user.language;
        }
      }
      if (value && value.locale) {
        this.locale = value.locale;
      }
    });
  }

  ngOnInit(): void {
    this.initRules();
  }

  ngOnDestroy(): void {
    if (this.tabExitHandler) {
      this.hostEl.nativeElement.removeEventListener('keydown', this.tabExitHandler, true);
      this.tabExitHandler = undefined;
    }
  }
  get ctSetName(){
    return `${this.mswfData.ctSet.id} ${this.mswfData.ctSet.name}`
  }
  initColumnDefs(){
    this.colDefs = [
      {
        headerName: null,
        headerValueGetter: () => this.translateSvc.instant("multistep.workflow.column.ct"),
        field: "displayName",
        tooltipField: "displayName",
        //cellRenderer: "TooltipField",
        flex:1,
        //colId: "plan_label_average",
        suppressMovable: true,

      },
      {
        headerName: null,
        headerValueGetter: () => this.translateSvc.instant("multistep.workflow.column.percent"),
        field: "distributionPercent",
        //cellRenderer: "TooltipField",
        //colId: "plan_label_average",
        suppressMovable: true,
        valueFormatter:this.cellValueFormatter,
        width:110,
      },
      {
        headerName: null,
        headerValueGetter: () => this.translateSvc.instant("multistep.workflow.column.offset"),
        field: "offsetDays",
        //cellRenderer: "TooltipField",
        //colId: "plan_label_average",
        suppressMovable: true,
        width:90
      }
    ]
  }
  dismiss() {
    this.activeModal.close();
  }

  onGridReady($event: GridReadyEvent) {
    this.gridApi = $event.api;
    // a11y (WCAG 4.1.2): AG Grid v31 defaults the root role to "treegrid".
    // This grid has no hierarchical/expandable rows, so downgrade to "grid"
    // to avoid screen readers announcing it as a tree grid.
    setFlatGridRole(this.hostEl.nativeElement);
    this.installTabExitToClose();
  }

  // a11y (WCAG 2.1.1 / 2.4.3): the shared exit-model handler moves focus to the
  // footer Close button when Tab leaves the grid, but after focus leaves the
  // grid other machinery (AG Grid's tab-guard AND ng-bootstrap's modal focus
  // trap) re-homes focus a tick later — from the CT header it lands on the
  // header X icon instead, so focus flashes onto Close then jumps to the X
  // (Shift+Tab is unaffected). This local handler forces focus onto the footer
  // Close button across a few ticks whenever Tab is pressed from inside the
  // grid, so the flash settles on Close.
  private installTabExitToClose(): void {
    const host = this.hostEl.nativeElement;
    this.tabExitHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.shiftKey) return;
      const grid = host.querySelector('.ag-root-wrapper') as HTMLElement | null;
      const active = host.ownerDocument?.activeElement as HTMLElement | null;
      if (!grid || !active || !grid.contains(active)) return;
      const closeBtn = host.querySelector('#accept-btn button') as HTMLButtonElement | null;
      if (!closeBtn) return;
      // Take over entirely so nothing else routes this Tab elsewhere.
      event.preventDefault();
      event.stopImmediatePropagation();
      const reassert = () => {
        const now = host.ownerDocument?.activeElement as HTMLElement | null;
        if (closeBtn.offsetParent !== null && now !== closeBtn) {
          closeBtn.focus();
        }
      };
      reassert();
      Promise.resolve().then(reassert);
      requestAnimationFrame(reassert);
      requestAnimationFrame(() => requestAnimationFrame(reassert));
    };
    host.addEventListener('keydown', this.tabExitHandler, true);
  }

  private initRules() {
    this.rules = this.mswfData.cts.filter((ct)=>!ct.initial).sort((ct1,ct2)=>ct1-ct2);
    let initialCT = {...this.mswfData.cts.find((ct)=>ct.initial),offsetDays:null,distributionPercent:null}

    //add initial CT to head
    this.rules.unshift(initialCT);
    //add displayName to every ct object
    this.rules = this.rules.map((ct)=>{return {displayName:`${ct.ctId} ${ct.ctName}`,...ct}});
  }
  public cellValueFormatter = (params: any) => {
    if (params && params.data) {
      return NumberUtils.formatNumberIfNecessary(params.value, this.locale, 4);
    }
  };
}
